import { GasPrice } from "@/data/types";

import { LGT_SERVER_URL } from "./env";

const shouldUseTestNet =
  LGT_SERVER_URL.includes("dev") || LGT_SERVER_URL.includes("localhost");

export enum Network {
  Ethereum = 1,
  EthereumSepolia = 11155111,
  Polygon = 137,
  PolygonAmoy = 80002,
  Avalanche = 43114,
  AvalancheFuji = 43113,
  Base = 8453,
  BaseSepolia = 84532,
  Lamina1 = 10849,
  Lamina1Fuji = 764984,
}

export const NetworkNameByChainId = {
  [Network.Ethereum]: "Ethereum",
  [Network.EthereumSepolia]: "Ethereum Testnet",
  [Network.Polygon]: "Polygon",
  [Network.PolygonAmoy]: "Polygon Testnet",
  [Network.Avalanche]: "Avalanche",
  [Network.AvalancheFuji]: "Avalanche Testnet",
  [Network.Base]: "Base",
  [Network.BaseSepolia]: "Base Testnet",
  [Network.Lamina1]: "Lamina1",
  [Network.Lamina1Fuji]: "Lamina1 Testnet",
} as const;

export const PRODUCTION_NETWORKS = [
  Network.Ethereum,
  Network.Polygon,
  Network.Avalanche,
  Network.Base,
  Network.Lamina1,
] as const;

export const STAGING_NETWORKS = [
  Network.EthereumSepolia,
  Network.PolygonAmoy,
  Network.AvalancheFuji,
  Network.BaseSepolia,
  Network.Lamina1Fuji,
] as const;

export const NetworkToGasToken = {
  [Network.Avalanche]: "avalanche-2",
  [Network.Polygon]: "matic-network",
  [Network.Ethereum]: "ethereum",
  [Network.Base]: "ethereum",
} as Record<Network, string>;

export const ENVIRONMENT_NETWORKS = shouldUseTestNet
  ? STAGING_NETWORKS
  : PRODUCTION_NETWORKS;

export const DEFAULT_NETWORK = shouldUseTestNet
  ? Network.AvalancheFuji
  : Network.Avalanche;

export const GAS_CALCULATOR_NETWORKS = [
  Network.Avalanche,
  Network.Polygon,
  Network.Ethereum,
  Network.Base,
] as const;

export const TX_BASE_URL_FOR_CHAIN_ID = {
  [Network.Ethereum]: "etherscan.io",
  [Network.EthereumSepolia]: "sepolia.etherscan.io",
  [Network.Polygon]: "polygonscan.com",
  [Network.PolygonAmoy]: "amoy.polygonscan.com",
  [Network.Avalanche]: "snowscan.xyz",
  [Network.AvalancheFuji]: "testnet.snowscan.xyz",
  [Network.Base]: "basescan.org",
  [Network.BaseSepolia]: "sepolia.basescan.org",
  [Network.Lamina1]: "subnets.avax.network/lamina1",
  [Network.Lamina1Fuji]: "subnets-test.avax.network/lamina1tes",
};

export const GasInfoByNetwork = {
  [Network.Ethereum]: {
    lowMinimum: 1, // historic minimums for gas
    lowLimit: 10, // max for low gas before indicator turns red and indicates high gas
    unit: "Gwei",
    decimals: 2,
  },
  [Network.Avalanche]: {
    lowMinimum: 1,
    lowLimit: 10,
    unit: "nAvax",
    decimals: 2,
  },
  [Network.Polygon]: {
    lowMinimum: 50,
    lowLimit: 150,
    unit: "Gwei",
    decimals: 1,
  },
  [Network.Base]: {
    lowMinimum: 0.001,
    lowLimit: 0.02,
    unit: "Gwei",
    decimals: 3,
  },
} as const;

export const GasRangeByNetwork = {
  [Network.Avalanche]: [GasInfoByNetwork[Network.Avalanche].lowMinimum, 100],
  [Network.Polygon]: [GasInfoByNetwork[Network.Polygon].lowMinimum, 1000],
  [Network.Ethereum]: [GasInfoByNetwork[Network.Ethereum].lowMinimum, 100],
  [Network.Base]: [GasInfoByNetwork[Network.Base].lowMinimum, 10],
} as Record<Network, [number, number]>;

export const generateAddressLink = (chainId: Network, address: string) => {
  return `https://${TX_BASE_URL_FOR_CHAIN_ID[chainId]}/address/${address}`;
};

export const hasGasInfo = (chainId: number) => {
  return !!GasInfoByNetwork[chainId as keyof typeof GasInfoByNetwork];
};

export const extractGasInfo = (
  gasPrice: GasPrice,
  chainId: keyof typeof GasInfoByNetwork,
) => {
  const suggestedMaxFeePerGas = Number(gasPrice.medium.suggestedMaxFeePerGas);
  const suggestedMaxPriorityFeePerGas = Number(
    gasPrice.medium.suggestedMaxPriorityFeePerGas,
  );
  const currentGas = suggestedMaxFeePerGas + suggestedMaxPriorityFeePerGas;
  const gasLow = currentGas < GasInfoByNetwork[chainId].lowLimit;

  return { gasLow, currentGas };
};

export const getGasDescription = (
  chainId: keyof typeof GasInfoByNetwork,
  gasLow: boolean,
) => {
  return gasLow
    ? `Transaction costs are currently normal between ${GasInfoByNetwork[chainId].lowMinimum}-${GasInfoByNetwork[chainId].lowLimit} ${GasInfoByNetwork[chainId].unit} and the ${NetworkNameByChainId[chainId]} network is stable.`
    : `Transaction costs are high and the ${NetworkNameByChainId[chainId]} network is congested. We suggest waiting for the network to stabilize under ${GasInfoByNetwork[chainId].lowLimit} ${GasInfoByNetwork[chainId].unit}.`;
};
